/**
 * apps/battle/client/src/terminal/screens/ForgotPasswordScreen.tsx
 *
 * Phase 4.2 M7 — forgot-password / request-reset screen.
 *
 * POST /api/auth/request-reset { email } — server **always returns 200**
 * regardless of whether the email is on file (anti-enumeration). We mirror
 * that: on any 200 response we show the same "if that email exists, we sent
 * a link" copy.
 */
import React, { useState } from 'react';
import { api, ApiError } from '../../api.js';
import { Spinner, ErrorBanner } from '../../widgets.js';
import { TerminalFrame } from '../components/TerminalFrame.js';
import { KeyBar, type KeyBarItem } from '../components/KeyBar.js';

interface ForgotPasswordScreenProps {
  onBackToLogin: () => void;
}

export function ForgotPasswordScreen({ onBackToLogin }: ForgotPasswordScreenProps) {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim() || !email.includes('@')) {
      setError('Enter a valid email.');
      return;
    }
    setError(null);
    setLoading(true);
    try {
      await api.post('/api/auth/request-reset', { email: email.trim() });
      setSubmitted(true);
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        setError('Too many requests. Please wait a few minutes.');
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }

  const keybar: KeyBarItem[] = [
    { label: 'Esc', description: 'back to login', onActivate: onBackToLogin },
  ];

  return (
    <TerminalFrame
      title={<>ECONQUEST &nbsp;&nbsp;─&nbsp;&nbsp;RESET PASSWORD</>}
      keybar={<KeyBar items={keybar} />}
    >
      <div style={{ maxWidth: 480, margin: '0 auto', paddingTop: 24 }}>
        {submitted ? (
          <>
            <h2 style={{ color: '#e6ebf2', fontSize: 18, marginTop: 0 }}>Check your inbox</h2>
            <p style={{ color: '#cdd5e0', lineHeight: 1.6 }}>
              If an account exists for <strong style={{ color: '#e6ebf2' }}>{email}</strong>,
              we've sent a password-reset link. The link expires in 30 minutes.
            </p>
            <p style={{ color: '#97a3b6', fontSize: 13 }}>
              Didn't get it? Check your spam folder, or try again in a few minutes.
            </p>
            <button className="btn btn-primary mt-4" type="button" onClick={onBackToLogin}>
              Back to sign in
            </button>
          </>
        ) : (
          <form onSubmit={handleSubmit}>
            <p style={{ color: '#cdd5e0', marginTop: 0, lineHeight: 1.6 }}>
              Enter the email you signed up with. We'll email you a link to reset your password.
            </p>
            <div className="form-field">
              <label className="form-label" htmlFor="fp-email">Email</label>
              <input
                id="fp-email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                autoFocus
              />
            </div>
            {error && <ErrorBanner msg={error} />}
            <button className="btn btn-primary w-full mt-4" type="submit" disabled={loading}>
              {loading ? <Spinner size={16} /> : 'Send reset link'}
            </button>
            <p style={{ marginTop: 16, fontSize: 13 }}>
              <a href="#" onClick={(e) => { e.preventDefault(); onBackToLogin(); }} style={{ color: '#3b82f6' }}>
                ← Back to sign in
              </a>
            </p>
          </form>
        )}
      </div>
    </TerminalFrame>
  );
}
